/**
 * @fileoverview Schema for Plot Threads.
 *
 * Plot threads are lightweight narrative segments that track specific
 * subplots, minor character arcs, or thematic explorations. They provide
 * structure without the full beat treatment used for major storylines.
 *
 * Plot threads emerge during chapter-level work (not planned upfront)
 * and are tracked with a simple question-based model.
 */

import { z } from 'zod';

// =============================================================================
// PLOT THREAD STATUS
// =============================================================================

/**
 * Status for plot threads.
 * - active: The thread is ongoing and unresolved
 * - resolved: The thread has been concluded
 * - abandoned: The thread was dropped (intentionally or not)
 */
export const PlotThreadStatusEnum = z.enum(['active', 'resolved', 'abandoned']);

// =============================================================================
// PLOT THREAD SCHEMA
// =============================================================================

/**
 * A plot thread is a lightweight narrative segment with a driving question.
 *
 * Example: "The Police Chase"
 * - Summary: "Captain Fache pursues Langdon through Paris, convinced he's guilty"
 * - Question: "Will they catch him before he proves his innocence?"
 * - Characters: [facheId, colletId, langdonId]
 */
export const PlotThreadSchema = z.object({
  id: z.string().describe('Unique identifier for this thread'),
  title: z.string().describe('Short, evocative title (e.g., "The Police Chase")'),
  summary: z.string().describe('What this thread is about - the context that makes the question meaningful'),
  threadQuestion: z.string().describe('The dramatic question driving this thread'),
  linkedCharacterIds: z.array(z.string()).describe('Characters involved in this thread'),

  // Chapter tracking (populated as story develops)
  introducedInChapterId: z.string().nullable().describe('Chapter where this thread was introduced'),
  resolvedInChapterId: z.string().nullable().describe('Chapter where this thread was resolved'),
  activeInChapterIds: z.array(z.string()).describe('Chapters where this thread advances'),

  // Status
  status: PlotThreadStatusEnum.default('active').describe('Current status of this thread'),
  corrupted: z.boolean().default(false).describe('Whether linkage/data integrity may be stale due to chapter deletion side effects'),

  // Origin — how this thread was created
  origin: z.enum(['planned', 'emergent']).optional().describe(
    'How this thread was created. "planned" = created during story planning. "emergent" = discovered during writing. undefined = legacy thread (treat as emergent).'
  ),

  // Metadata
  createdAt: z.string().describe('ISO timestamp when this thread was created'),
  updatedAt: z.string().describe('ISO timestamp when this thread was last updated'),
});

// =============================================================================
// TYPE INFERENCE
// =============================================================================

export type PlotThreadStatus = z.infer<typeof PlotThreadStatusEnum>;
export type PlotThread = z.infer<typeof PlotThreadSchema>;

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Creates a new plot thread with defaults.
 */
export function createPlotThread(
  title: string,
  summary: string,
  threadQuestion: string,
  introducedInChapterId?: string
): PlotThread {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title,
    summary,
    threadQuestion,
    linkedCharacterIds: [],
    introducedInChapterId: introducedInChapterId ?? null,
    resolvedInChapterId: null,
    activeInChapterIds: introducedInChapterId ? [introducedInChapterId] : [],
    status: 'active',
    corrupted: false,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Hydrates a flat plot thread output from an agent into a full PlotThread entity.
 *
 * Agents output a minimal shape (title, summary, threadQuestion).
 * This function adds system fields: ID, timestamps, chapter linkage, and origin.
 */
export function hydratePlotThread(
  output: { title: string; summary: string; threadQuestion: string },
  chapterId: string
): PlotThread {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title: output.title,
    summary: output.summary,
    threadQuestion: output.threadQuestion,
    linkedCharacterIds: [],
    introducedInChapterId: chapterId,
    resolvedInChapterId: null,
    activeInChapterIds: [chapterId],
    status: 'active',
    corrupted: false,
    origin: 'planned',
    createdAt: now,
    updatedAt: now,
  };
}
